import React, { useEffect } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { ViewPropertyReport } from '@/reports/shared/ViewPropertyReport/ViewPropertyReport';
import { useSearchListing } from '@/search/index';
import {
  Modal,
  CircularProgress,
  Box,
  styled,
  Button,
  Stack,
  Text,
} from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';
import {
  ListingDetailsContactAgent,
  ListingDetailsImagesCarousel,
  ListingDetailsOverview,
  ListingDetailsSales,
  ListingDetailsNotes,
  ListingDetailsFeaturesSection,
  SectionDivider,
} from './index';
import { ListingDetailsLeadsForm } from '../../ListingDetailsLeadsForm/ListingDetailsLeadsForm';

const ViewReportButton = styled(Button)(({ theme }) => ({
  width: '100%',
  minWidth: '239px',
  padding: theme.spacing(1, 2.75),
  borderRadius: '4px',
}));

interface ListingDetailsModalBodyProps {
  open: boolean;
  onClose: () => void;
  listingKey: string;
}

export function ListingDetailsModalBody({
  open,
  onClose,
  listingKey,
}: ListingDetailsModalBodyProps) {
  const { isMobile } = useScreenSize();
  const {
    data: { brandAbbreviation, brandName },
  } = useAppConstantsContext();
  const { trackListHub } = useAppConfigContext();

  const listhubEventsMap: { [alias: string]: string } = {
    detail_page: 'DETAIL_PAGE_VIEWED',
    agent_website: 'AGENT_WEBSITE_CLICKED',
    office_website: 'OFFICE_WEBSITE_CLICKED',
    viewed_more_photos: 'VIEWED_MORE_PHOTOS',
  };

  useEffect(() => {
    if (listingKey) {
      // NOTE: This event needs to be sent just once (when the modal is opened)
      // That's why we don't add anything to the dependency array.
      // If you need to execute something when the component is mounted and
      // need to add a dependency to the array then use another useEffect hook
      trackListHubInfo('detail_page');
    }
  }, []);

  const { data, isFetched } = useSearchListing({
    listingKey,
    brandAbbreviation,
  });

  const trackListHubInfo = (eventAlias: string) => {
    const listhubEventName = listhubEventsMap[eventAlias];
    if (trackListHub && listingKey && listhubEventName) {
      trackListHub(listhubEventName, listingKey);
    }
  };

  return (
    <Modal
      className="listing-details-modal"
      data-testid="listing-details-modal"
      open={open}
      onClose={onClose}
      hasCloseIcon={!!isFetched}
      maxWidth={false}
      closeButtonProps={{
        sx: {
          top: 20,
          right: 18,
          '& > svg': { fontSize: 35, color: 'primary.main' },
        },
      }}
      sx={{
        '.carousel-div-wrapper': { marginTop: '55px' },
      }}
    >
      {!data ? (
        <CircularProgress size={92} thickness={4} disableShrink />
      ) : (
        <Box width="100%" maxWidth="930px">
          <ListingDetailsImagesCarousel
            images={data.images}
            broker={data.brokerage}
            trackListHubInfo={trackListHubInfo}
          />
          <ListingDetailsSales
            status={data.transactionStatus}
            address={data.address}
            price={data.price}
            bedrooms={data.building?.totalBeds}
            bathrooms={data.building?.totalBaths}
            livingSpace={data.building?.livingSpace}
            transactionType={data.transactionType}
          />
          <Stack
            marginTop={2.5}
            flexDirection="row"
            flexWrap="wrap"
            alignItems="center"
            gap={2.5}
          >
            <ViewPropertyReport
              address={data.rawAddress}
              sx={{
                flexBasis: isMobile ? '100%' : '26%',
              }}
            >
              <ViewReportButton variant="contained" color="success">
                View Property Report
              </ViewReportButton>
            </ViewPropertyReport>
            <ListingDetailsContactAgent
              brokerage={data.brokerage}
              trackListHubInfo={trackListHubInfo}
            />
          </Stack>
          <SectionDivider />
          <Text variant="h3" mb={1.5}>
            Overview
          </Text>
          <ListingDetailsOverview
            listingType={data.listingType}
            status={data.transactionStatus}
            mls={data.brokerage?.mls}
            listDate={data.listDate}
            propertyType={data.subType}
            building={data.building}
          />
          <SectionDivider />
          <Text variant="h3" mb={1.5}>
            About {data.address?.parts?.streetName}
          </Text>
          <ListingDetailsNotes notes={data.notes} brokerage={data.brokerage} />
          <SectionDivider />
          <ListingDetailsFeaturesSection
            listingType={data.listingType}
            appliances={data.appliances}
            communityFeatures={data.communityFeatures}
            heating={data.heating}
            cooling={data.cooling}
            flooring={data.flooring}
            exteriorFeatures={data.exteriorFeatures}
            taxes={data.taxes}
          />
          <SectionDivider />
          {data.disclaimer ? (
            <Text fontSize="0.75rem" color="text.secondary">
              {data.disclaimer}
            </Text>
          ) : (
            <Text fontSize="0.75rem" color="text.secondary">
              &copy; {new Date().getFullYear()}{' '}
              <Text component="span" fontSize="inherit" fontWeight="bold">
                {brandName} LLC
              </Text>{' '}
              all rights reserved. {brandName}®, and the {brandName} fence logo
              are registered trademarks of {brandName} LLC. All information
              provided by the listing agent/broker is deemed reliable but is not
              guaranteed and should be independently verified.
            </Text>
          )}
          <SectionDivider />
          <ListingDetailsLeadsForm propertyData={data} />
        </Box>
      )}
    </Modal>
  );
}
